import groupBy from 'object.groupby'
import { create } from 'zustand'

import { computed } from '@/stores/computed'
import { $data } from '@/stores/Data'
import { DELUXE_SINGLE_PROBE_HOLDER, ITEMS_PRESELECTED } from '@/util/Hardcoded'

export type Item = {
  partId: string
  name: string
  rNumber: string
  categoryId: string
  hubId: string
  amount: number
  price: number
  preselect: boolean
  preselectAmount: number // Original preselected quantity (never changes)
  currency: string
  limit: number
}

type HubId = string

type CartStore = {
  items: Item[]

  revalidate: () => void
  initialize: () => void
  totalPerHub: () => Record<HubId, number>
  getByRNumber: (rNumber: string) => Item | undefined
  getByRNumberAndHub: (hubId: string, rNumber: string) => Item | undefined
  increase: (item: Item) => void
  decrease: (item: Item) => void
  reset: () => void
}

const generateCartFromJSON = (): Item[] => {
  const data = $data.get()
  if (!data) return []
  const items: Item[] = []

  for (const hub of data.hubs) {
    for (const category of hub.categories) {
      for (const part of category.parts) {
        const isPreselected = ITEMS_PRESELECTED.includes(part.id)
        const isDeluxeProbeHolder = part.id === DELUXE_SINGLE_PROBE_HOLDER
        const initialAmount = isDeluxeProbeHolder ? 3 : isPreselected ? 1 : 0

        items.push({
          amount: initialAmount,
          categoryId: category.id,
          name: part.partname,
          hubId: hub.id,
          partId: part.id,
          preselect: isPreselected,
          preselectAmount: initialAmount, // Store original preselected quantity
          rNumber: part.RNumber,
          price: Number.parseFloat(part.pricing.retailprice ?? '0'),
          currency: part.pricing.currency,
          limit: part.part_limit,
        })
      }
    }
  }

  return items
}

const useCartStore = create<CartStore>()((set, get) => ({
  items: [],
  totalPerHub: computed(
    () => [get().items, $data.get()],
    (items, data) => {
      if (!data) return {} as Record<string, number>
      return Object.fromEntries(
        Object.entries(
          groupBy(items, item => item.hubId),
        ).map(([hubId, hubItems]) => [
          hubId,
          hubItems.reduce((acc, item) => {
            // Only charge for quantities above the preselected amount (user's additions)
            // preselectAmount items are "free" / included with the hub
            const chargeableAmount = Math.max(0, item.amount - item.preselectAmount)
            return acc + item.price * chargeableAmount
          }, 0),
        ] as [string, number])
          .map(([hubId, priceWithoutHub]) => [
            hubId,
            priceWithoutHub + Number.parseFloat(data.hubs.find(h => h.id === hubId)?.pricing?.retailprice || '0'),
          ])
        ,
      ) as Record<string, number>
    },
  ),

  initialize: () => get().reset(),
  reset: () => set({
    items: generateCartFromJSON(),
  }),
  getByRNumber: rNumber => get().items.find(item => item.rNumber === rNumber),
  getByRNumberAndHub: (hubId, rNumber) => get().items.find(item => item.hubId === hubId && item.rNumber === rNumber),

  revalidate: () => {
    const items = get().items
    const data = $data.get()!

    const updatedItems = items.map(item => {
      const part = data.hubs.find(h => h.id === item.hubId)?.categories.flatMap(c => c.parts).find(p => p.id === item.partId)
      if (!part) return item
      return {
        ...item,
        name: part.partname,
        rNumber: part.RNumber,
        price: Number.parseFloat(part.pricing.retailprice ?? '0'),
        currency: part.pricing.currency,
      }
    })
    return set({ items: updatedItems })
  },

  increase: newItem => set((state) => {
    const itemIndex = state.items.findIndex(item => item.partId === newItem.partId)
    const itemExists = itemIndex !== -1
    const existingItem = state.items[itemIndex]
    const amount = (existingItem?.amount ?? 0) + 1
    const item = {
      ...newItem,
      amount,
      // Preserve preselectAmount from existing item, or use newItem's value, or default to 0
      preselectAmount: existingItem?.preselectAmount ?? newItem.preselectAmount ?? 0,
    }

    // ? Create
    if (itemExists) {
      state.items[itemIndex] = item
      return { ...state, items: [...state.items] }
    }

    return {
      ...state,
      items: [...state.items, item],
    }
  }),

  decrease: newItem => set((state) => {
    const itemIndex = state.items.findIndex(item => item.partId === newItem.partId)
    const itemExists = itemIndex !== -1
    const existingItem = state.items[itemIndex]
    const amount = (existingItem?.amount ?? 0) - 1
    const item = {
      ...newItem,
      amount,
      // Preserve preselectAmount from existing item, or use newItem's value, or default to 0
      preselectAmount: existingItem?.preselectAmount ?? newItem.preselectAmount ?? 0,
    }

    // ? Remove
    if (amount < 1) {
      // return {
      //   ...state,
      //   items: state.items.filter(item => item.partId !== newItem.partId),
      // }
      item.amount = 0
      state.items[itemIndex] = item
      return { ...state, items: [...state.items] }
    }

    if (itemExists) {
      state.items[itemIndex] = item
      return { ...state, items: [...state.items] }
    }

    return {
      ...state,
      items: [...state.items, item],
    }
  }),
}
))

export default useCartStore
